import React from 'react';
import { ImageFadeIn } from '../components/animations/ImageFadeIn';
import { ScrollReveal } from '../components/animations/ScrollReveal';
import { Target, School, Home, Building, Siren, Building2, Heart, Users, ShieldCheck, AlertTriangle } from 'lucide-react';
const TrainingCenterPage = () => {
  return <div className="w-full min-h-screen bg-white" data-id="element-1109">
      {/* Hero Section - Reduced height */}
      <div className="relative min-h-[50vh] flex items-center" data-id="element-1110">
        <div className="absolute inset-0 z-0" data-id="element-1111">
          <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/dCpAxqefTErEsMjEUAy1YR/istockphoto-1738959959-612x612.jpg" alt="Israeli military personnel walking with flag" className="w-full h-full object-cover" data-id="element-1112" />
          <div className="absolute inset-0 bg-black/60" data-id="element-1113"></div>
        </div>
        <div className="relative z-10 w-full pt-36 sm:pt-52 md:pt-56 pb-12 sm:pb-16" data-id="element-1114">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-1115">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-3 sm:mb-4" data-id="element-1116">
              Regional Civilian Training Centers
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1117">
              Building a safer Israel through education, preparation, and
              community resilience
            </p>
          </div>
        </div>
      </div>
      {/* Mission Statement - Reduced padding */}
      <ScrollReveal animation="fade" data-id="element-1118">
        <div className="bg-blue-50 py-10 sm:py-16" data-id="element-1119">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-1120">
            <div className="inline-flex items-center justify-center mb-4" data-id="element-1121">
              <Heart className="w-8 h-8 text-red-500 mr-3" data-id="element-1122" />
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" data-id="element-1123">
                Our Mission
              </h2>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed mb-6 max-w-3xl mx-auto" data-id="element-1124">
              The Samaria Regional Training Center (SRTC) will serve as the
              first permanent hub for civilian security and emergency response
              training, offering free instruction to Israeli civilians. This
              state-of-the-art facility is designed to equip civilians with the
              critical, life-saving skills needed to survive—and thrive—in a
              volatile environment.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-6" data-id="element-1125">
              <div className="flex items-center" data-id="element-1126">
                <div className="p-2 rounded-full bg-blue-100 mr-2" data-id="element-1127">
                  <ShieldCheck className="w-5 h-5 text-blue-600" data-id="element-1128" />
                </div>
                <span className="text-gray-700" data-id="element-1129">
                  No or Minimal Cost Training for All Civilians
                </span>
              </div>
              <div className="flex items-center" data-id="element-1130">
                <div className="p-2 rounded-full bg-blue-100 mr-2" data-id="element-1131">
                  <Users className="w-5 h-5 text-blue-600" data-id="element-1132" />
                </div>
                <span className="text-gray-700" data-id="element-1133">50,000 Trained Annually</span>
              </div>
              <div className="flex items-center" data-id="element-1134">
                <div className="p-2 rounded-full bg-blue-100 mr-2" data-id="element-1135">
                  <Target className="w-5 h-5 text-blue-600" data-id="element-1136" />
                </div>
                <span className="text-gray-700" data-id="element-1137">Professional Instructors</span>
              </div>
            </div>
            <p className="text-lg text-gray-700" data-id="element-1138">
              No comparable center currently exists in Israel, and the
              development of this facility will establish a powerful and
              sustained capability to ensure the local population is fully
              prepared for all types of crisis situations.
            </p>
          </div>
        </div>
      </ScrollReveal>
      {/* Main Content - Reduced spacing between sections */}
      <div className="py-16 sm:py-24" data-id="element-1139">
        <div className="max-w-6xl mx-auto px-4 sm:px-6" data-id="element-1140">
          {/* Live-Fire Ranges Section */}
          <ScrollReveal animation="fade" data-id="element-1141">
            <div className="mb-24" data-id="element-1142">
              <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-1143">
                <div className="w-full md:w-3/5" data-id="element-1144">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/k35j6YjqWVjKpJmHa5WRo3/000_QQ3AP-e1502868300864.webp" alt="Civilians practicing with firearms in a training environment" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1145" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1146">
                  <div className="flex items-center mb-4" data-id="element-1147">
                    <Target className="w-8 h-8 text-blue-500 mr-3" data-id="element-1148" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1149">
                      Live-Fire Training Ranges
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1150">
                    Our facility features three dedicated civilian training
                    ranges: a 100-meter rifle range and 2 25-meter pistol range.
                    Both are equipped with advanced lighting systems and moving
                    targets to create realistic training scenarios that prepare
                    civilians for real-world threats.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1151">
                    Unlike commercial ranges, our facilities are specifically
                    designed for civilian defense training, with instructors who
                    understand the unique challenges faced by non-military
                    personnel in crisis situations.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* Classroom and Simulation Section */}
          <ScrollReveal animation="fade" data-id="element-1152">
            <div className="mb-24" data-id="element-1153">
              <div className="flex flex-col md:flex-row-reverse gap-12 items-center" data-id="element-1154">
                <div className="w-full md:w-3/5" data-id="element-1155">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/9kZniEp5pHeQCp7B21FM72/Screenshot_2025-06-07_at_6.21.43_PM.png" alt="Education and simulation training" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1156" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1157">
                  <div className="flex items-center mb-4" data-id="element-1158">
                    <School className="w-8 h-8 text-blue-500 mr-3" data-id="element-1159" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1160">
                      Education & Simulation
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1161">
                    Our 20,000 square foot classroom complex houses
                    state-of-the-art MILO simulators that provide immersive,
                    scenario-based training in a controlled environment. These
                    advanced systems allow civilians to practice decision-making
                    and response techniques without risk.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1162">
                    The educational curriculum combines theoretical knowledge
                    with practical application, ensuring that every participant
                    gains not just skills, but the confidence to use them
                    effectively in emergency situations.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* Tactical Village Section */}
          <ScrollReveal animation="fade" data-id="element-1163">
            <div className="mb-24" data-id="element-1164">
              <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-1165">
                <div className="w-full md:w-3/5" data-id="element-1166">
                  <ImageFadeIn src="https://cdn.magicpatterns.com/uploads/e43pnZZjr473Xb5M9jzvyi/3B33D28A-2AE4-4CE9-83F7-10F66D2901A1e9f91f72-3991-4691-ad21-7b3d91cac443.jpg" alt="Aerial view of Combat Town tactical training village" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1167" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1168">
                  <div className="flex items-center mb-4" data-id="element-1169">
                    <Building className="w-8 h-8 text-blue-500 mr-3" data-id="element-1170" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1171">
                      Tactical Training Village
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1172">
                    Our Combat Town training village replicates real urban
                    environments with integrated roadways, buildings, and
                    obstacles. This facility allows civilians to practice home
                    defense, community protection, and emergency evacuation in
                    realistic settings.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1173">
                    Participants learn to navigate complex scenarios including
                    multiple threats, civilian protection, and coordination with
                    neighbors—skills that have proven critical during actual
                    attacks on Israeli communities.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* Medical Training Section */}
          <ScrollReveal animation="fade" data-id="element-1174">
            <div className="mb-24" data-id="element-1175">
              <div className="flex flex-col md:flex-row-reverse gap-12 items-center" data-id="element-1176">
                <div className="w-full md:w-3/5" data-id="element-1177">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/thhZHdVXq4qvhzzQjtRD9a/%D7%A6%D7%99%D7%9C%D7%95%D7%9D-%D7%99%D7%97%D7%99%D7%90%D7%9C-%D7%92%D7%95%D7%A8%D7%A4%D7%99%D7%99%D7%9F-127-1024x683.jpg" alt="Medical emergency response training" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1178" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1179">
                  <div className="flex items-center mb-4" data-id="element-1180">
                    <Siren className="w-8 h-8 text-blue-500 mr-3" data-id="element-1181" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1182">
                      Medical Emergency Response
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1183">
                    Our trauma-simulation lab features high-fidelity medical
                    manikins that replicate realistic injuries and physiological
                    responses. Civilians learn critical first-aid skills
                    including tourniquet application, wound packing, and
                    casualty evacuation.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1184">
                    When emergency services are delayed—as often happens in
                    remote communities or during large-scale attacks—these
                    medical skills can save lives in the critical first minutes
                    after injury.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* All-Hazard Response Training Section */}
          <ScrollReveal animation="fade" data-id="element-1185">
            <div className="mb-24" data-id="element-1186">
              <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-1187">
                <div className="w-full md:w-3/5" data-id="element-1188">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/ktjBBrrjxDcrx4BJceBNW7/Screenshot_2025-08-06_at_7.55.02_PM.png" alt="Emergency response personnel conducting search and rescue operations at a structural collapse" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1189" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1190">
                  <div className="flex items-center mb-4" data-id="element-1191">
                    <AlertTriangle className="w-8 h-8 text-blue-500 mr-3" data-id="element-1192" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1193">
                      All-Hazard Response Training
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1194">
                    Our specialized facilities include basic training for
                    chemical-radiological defense, fully equipped safe rooms for
                    shelter-in-place training, and a dedicated area for
                    practicing evacuation procedures during fires, explosions,
                    and natural disasters.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1195">
                    The center features a structural collapse simulator where
                    civilians learn basic search and rescue techniques. These
                    facilities enable civilians to practice earthquake response
                    protocols and coordinate with security operations during
                    emergencies.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
      {/* Accommodation Section */}
      <ScrollReveal animation="fade" data-id="element-1196">
        <div className="bg-gray-50 py-10 sm:py-16" data-id="element-1197">
          <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-1198">
            <div className="text-center mb-8" data-id="element-1199">
              <div className="inline-flex items-center justify-center mb-3" data-id="element-1200">
                <Home className="w-7 h-7 text-blue-500 mr-2" data-id="element-1201" />
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-id="element-1202">
                  On-Site Accommodations
                </h2>
              </div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-1203">
                Supporting multi-day training programs for families and
                communities
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1204">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-1205">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-full" data-id="element-1206">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1207">
                    Residential Facilities
                  </h3>
                  <p className="text-base text-gray-700 mb-4 leading-relaxed" data-id="element-1208">
                    Our 25-bed dormitory includes 10 twin rooms for individual
                    participants and 5 queen rooms for couples, allowing
                    families to train together and build collective resilience.
                  </p>
                  <p className="text-base text-gray-700 leading-relaxed" data-id="element-1209">
                    The facility is designed to accommodate multi-day intensive
                    training programs, particularly for participants from remote
                    communities who would otherwise be unable to access quality
                    training.
                  </p>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-1210">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-full" data-id="element-1211">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1212">
                    Dining & Community Spaces
                  </h3>
                  <p className="text-base text-gray-700 mb-4 leading-relaxed" data-id="element-1213">
                    Our full-service dining hall can serve up to 150 people,
                    supporting not just training participants but also community
                    events and emergency response coordination.
                  </p>
                  <p className="text-base text-gray-700 leading-relaxed" data-id="element-1214">
                    These spaces are designed to foster community connections
                    and build the social fabric that becomes crucial during
                    crisis situations, when neighbors must rely on each other.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Impact & Capacity */}
      <ScrollReveal animation="fade" data-id="element-1215">
        <div className="bg-israel-blue text-white py-10 sm:py-16" data-id="element-1216">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-1217">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 tracking-tight" data-id="element-1218">
              Our Impact
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10" data-id="element-1219">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-1220">
                <div data-id="element-1221">
                  <div className="text-4xl sm:text-5xl font-bold mb-2" data-id="element-1222">7</div>
                  <p className="text-base opacity-90 font-light" data-id="element-1223">
                    Full-time Israeli instructors with ability to scale
                  </p>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-1224">
                <div data-id="element-1225">
                  <div className="text-4xl sm:text-5xl font-bold mb-2" data-id="element-1226">50K</div>
                  <p className="text-base opacity-90 font-light" data-id="element-1227">
                    Civilians trained annually at minimal to no cost
                  </p>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.3} data-id="element-1228">
                <div data-id="element-1229">
                  <div className="text-4xl sm:text-5xl font-bold mb-2" data-id="element-1230">3</div>
                  <p className="text-base opacity-90 font-light" data-id="element-1231">
                    Future centers planned across Israel
                  </p>
                </div>
              </ScrollReveal>
            </div>
            <div data-id="element-1232">
              <a href="/roadmap" className="inline-block px-6 py-3 bg-white text-israel-blue font-semibold rounded-lg hover:bg-gray-100 transition-colors text-base shadow-lg" data-id="element-1233">
                View Our Implementation Timeline
              </a>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* CTA Section */}
      <ScrollReveal animation="fade" data-id="element-1234">
        <div className="py-10 sm:py-16 bg-white" data-id="element-1235">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-1236">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight" data-id="element-1237">
              Help Us Build This Vital Resource
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto" data-id="element-1238">
              Your support can help us break ground on this life-saving facility
              sooner. Every day we wait is another day Israeli civilians remain
              vulnerable.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-1239">
              <a href="https://donate.stripe.com/7sY5kw7P1eO24x90y314401" target="_blank" rel="noopener noreferrer" className="inline-block px-6 py-3 bg-israel-blue text-white font-semibold rounded-lg hover:bg-israel-blue-light transition-colors text-base shadow-lg" data-id="element-1240">
                Donate Now
              </a>
              <a href="/support" className="inline-block px-6 py-3 bg-white text-israel-blue font-semibold rounded-lg border-2 border-israel-blue hover:bg-gray-50 transition-colors text-base" data-id="element-1241">
                Support Our Mission
              </a>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>;
};
export default TrainingCenterPage;